import { useToken } from "@gluestack-style/react";
import CheckoutProgress from "components/general/CheckoutProgress";
import Container from "components/general/Container";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import ItemCardList from "components/item/ItemCardList";
import { Box, Button, H2, H3, HStack, Paragraph, Typography } from "components/ui";
import { History, PartyPopper } from "lucide-react-native";
import React from "react";
import { useGetRecommendedQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useCheckoutResponse } from "utils/hooks/useCheckoutResponse";
import { ActivityIndicator } from "react-native";

export default function Page() {
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const { checkoutResponse, isLoading } = useCheckoutResponse();
	const { data: recommendedProductCollection } = useGetRecommendedQuery();

	const getApprovalReasonMessage = (reason: string | null) => {
		if (!reason) return "";

		switch (reason) {
			case "ALL_ORDERS":
				return "approval required for all orders";
			case "NON_STOCKLIST":
				return "approval required for non stocklist items";
			case "NON_CATALOGUE":
				return "approval required for non catalogue items";
			case "OVER_BUDGET":
				return "approval required for over budget orders";
			case "URGENT_FASTEST":
				return "approval required for urgent/fastest shipping";
		}

		return "";
	};

	const isMobile = useIsMobile();
	return (
		<ScrollContainer>
			<Container>
				<CheckoutProgress />
				<Box bg="$basicwhite" alignItems="center">
					<Box w="$full" maxWidth={863} gap={48} padding={isMobile ? 24 : 48}>
						{isLoading ? (
							<Box paddingVertical={24} gap={48} alignItems="center">
								<ActivityIndicator style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }, { translateY: 0 }] }} size="large" color={resolvedPrimarylight} />
								<Typography.H3>Processing your order...</Typography.H3>
							</Box>
						) : (
							<>
								<Box alignItems="center">
									{checkoutResponse && checkoutResponse.type === "Approval" && (
										<>
											<History width={60} height={60} color={resolvedPrimarylight} />
											<H2 sx={{ lineHeight: isMobile ? 24 : 48 }}>Order sent for approval</H2>
											<Typography sx={{ fontSize: isMobile ? 16 : 24, marginTop: 10 }}>Reference number: CRT{checkoutResponse!.id}</Typography>
											<Typography sx={{ marginTop: 10 }} weight="bold">
												Your order is now awaiting approval
											</Typography>
											<Typography weight="bold">Reason: {getApprovalReasonMessage(checkoutResponse!.reason)}</Typography>
										</>
									)}
									{checkoutResponse && checkoutResponse.type === "Success" && (
										<>
											<PartyPopper width={60} height={60} color={resolvedPrimarylight} />
											<H2 sx={{ lineHeight: isMobile ? 24 : 48 }}>Order submitted</H2>
											{checkoutResponse!.recordType === "SalesOrder" && <Typography sx={{ fontSize: isMobile ? 16 : 24, marginTop: 10 }}>Reference number: S{checkoutResponse!.id}</Typography>}

											{checkoutResponse!.recordType !== "SalesOrder" && <Typography sx={{ fontSize: isMobile ? 16 : 24, marginTop: 10 }}>Reference number: CRT{checkoutResponse!.id}</Typography>}

											<Typography sx={{ marginTop: 10 }} weight="bold">
												Your order is now being processed
											</Typography>
											<Typography weight="bold">Your will receive an email notification shortly</Typography>
										</>
									)}
								</Box>
								<HStack gap={8} alignItems="center" justifyContent="center">
									{/* <Button flex={1} borderWidth={1} paddingVertical={12} borderColor="$primarylight">
								<Paragraph sx={{ color: "$primarylight" }}>Create new order</Paragraph>
							</Button> */}
									<Box maxWidth={200}>
										<Button variant="primary" isLink href="/orders" flex={1} borderWidth={1} bg="$primarylight">
											<Paragraph sx={{ color: "$basicwhite" }}>View all orders</Paragraph>
										</Button>
									</Box>
								</HStack>
							</>
						)}
						{/* <Box borderRadius={8} padding={isMobile ? 12 : 24} bg="$neutral25">
							<Typography sx={{ marginBottom: 16 }} weight="bold">
								Select order recurrence
							</Typography>
							<Divider bg="$neutral100" marginBottom={16} />
							<ParagraphSmall sx={{ lineHeight: 20 }} weight="normal">
								Setting up an order as a recurring order ensures your favourite items are delivered to your site regularly, providing both convenience and peace of mind.
							</ParagraphSmall>
							<Box>
								<SwitchBasic text="Repeat order" />
							</Box>
						</Box> */}
					</Box>
				</Box>
				<Box padding={isMobile ? 12 : 48} gap={24} alignItems="center">
					{isMobile ? (
						<Container noPadding>
							<Box gap={24} mt={24}>
								<H3>Recommended For you</H3>
								<ItemCardList isGridView={false} isOnNavigation list={recommendedProductCollection} />
							</Box>
						</Container>
					) : (
						<Section>
							<H2>Recommended</H2>
							<ItemCardList list={recommendedProductCollection} />
						</Section>
					)}
				</Box>
			</Container>
		</ScrollContainer>
	);
}
